/**
 * @file ContentCategoriesPage.tsx
 * @description CRUD категорий блога (phase12.0).
 *
 * Р3в (v0.19.64, SB131): «Изменить» — окно `ContentCategoryEditDialog`
 * (название, описание, адрес с галочкой «Переадресовать со старого адреса»).
 */

import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Input } from "../../../components/ui/input";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { contentCategories, type ContentCategory } from "../../../lib/api-content";
import { ContentCategoryEditDialog } from "./ContentCategoryEditDialog";
import { invalidatePickList } from "../../../hooks/site-builder/usePickList";

export function ContentCategoriesPage() {
  const notify = useNotify();
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState<ContentCategory | null>(null);

  async function refresh() {
    // Рубрики изменились — список целей переадресации и меню спросит заново.
    invalidatePickList("contentCategories");
    setLoading(true);
    try {
      setCategories(await contentCategories.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim() || !draft.description.trim()) return;
    setSubmitting(true);
    try {
      await contentCategories.create({
        name: draft.name.trim(),
        description: draft.description.trim(),
      });
      setDraft({ name: "", description: "" });
      notify.success("Категория создана");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(c: ContentCategory) {
    if (!confirm(`Удалить категорию «${c.name}»?`)) return;
    try {
      await contentCategories.remove(c.id);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Контент · Категории</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Категории блога</h1>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <form onSubmit={(e) => void onCreate(e)} className="flex flex-col gap-3 mb-6">
            <Input
              placeholder="Название категории"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              disabled={submitting}
            />
            <Input
              placeholder="Описание (1-2 предложения)"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              disabled={submitting}
            />
            <Button
              type="submit"
              disabled={submitting || !draft.name.trim() || !draft.description.trim()}
              className="self-start"
            >
              Добавить категорию
            </Button>
          </form>

          {loading ? (
            <p className="text-sm text-on-surface-muted">Загрузка…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-on-surface-muted">Категорий нет.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-on-surface/10">
              {categories.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-4 py-3">
                  <div className="flex-1">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-sm text-on-surface-muted">{c.description}</div>
                    <div className="text-xs text-on-surface-muted mt-1">
                      /blog/category/{c.slug}
                      {c.articlesCount !== undefined && ` · ${c.articlesCount} статей`}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="size-12 shrink-0 lg:size-9" aria-label={`Изменить ${c.name}`} onClick={() => setEditing(c)}>
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                  <button
                    type="button"
                    onClick={() => void onDelete(c)}
                    className="text-on-surface-muted hover:text-rose-600"
                    aria-label={`Удалить ${c.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </EditorialCard.Body>
      </EditorialCard>
      <ContentCategoryEditDialog category={editing} onClose={() => setEditing(null)} onSaved={() => void refresh()} />
    </div>
  );
}
